import { useInfiniteQuery } from '@tanstack/react-query';
import { formatDate } from 'date-fns';
import { ILeadSearchView } from '../../aida/viewModels/leads/interfaces';
import { IPagedCollection } from '../../extViewmodels';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { LevStandardQueryMeta } from '../../web/ReactQueryProvider';

const BASE_QUERY_KEY = 'lead/demosToConfirm';

export function useLeadDemosQuery({
	pageSize = 25,
	userId,
	outcomeName,
	demoPerformerUserId,
	start,
	end,
	enabled,
	managerUserId,
	companyId,
	dealStage,
	meta,
	// Past Demos tab filters
	leadSource,
	addOn,
	inboundChannel,
	lostReasonCategories,
	industry,
	tag,
	isColdDemo,
	overbook,
	followUpDateStart,
	followUpDateEnd,
	targetCloseDateStart,
	targetCloseDateEnd,
	closedDateStart,
	closedDateEnd,
}: {
	userId?: string;
	outcomeName?: string;
	demoPerformerUserId?: string;
	start?: Date;
	end?: Date;
	pageSize?: number;
	enabled?: boolean;
	managerUserId?: string;
	companyId?: string;
	dealStage?: string[];
	meta?: LevStandardQueryMeta;
	// Past Demos tab filters
	leadSource?: string;
	addOn?: string;
	inboundChannel?: string;
	lostReasonCategories?: string;
	industry?: string;
	tag?: string;
	isColdDemo?: boolean;
	overbook?: boolean;
	followUpDateStart?: Date;
	followUpDateEnd?: Date;
	targetCloseDateStart?: Date;
	targetCloseDateEnd?: Date;
	closedDateStart?: Date;
	closedDateEnd?: Date;
}) {
	const userSession = useUserSession();
	return useInfiniteQuery({
		queryKey: [
			BASE_QUERY_KEY,
			userId,
			outcomeName,
			demoPerformerUserId,
			start,
			end,
			managerUserId,
			companyId,
			dealStage,
			leadSource,
			addOn,
			inboundChannel,
			lostReasonCategories,
			industry,
			tag,
			isColdDemo,
			overbook,
			followUpDateStart,
			followUpDateEnd,
			targetCloseDateStart,
			targetCloseDateEnd,
			closedDateStart,
			closedDateEnd,
		],
		queryFn: async ({ pageParam }) => {
			const searchParams = new URLSearchParams();
			const params: Record<string, string | undefined> = {
				pageSize: pageSize.toString(),
				userId,
				outcomeName,
				demoPerformerUserId,
				start: start ? formatDate(start, 'yyyy-MM-dd') : undefined,
				end: end ? formatDate(end, 'yyyy-MM-dd') : undefined,
				managerUserId,
				companyId,
				pageToken: pageParam,
				// Past Demos tab filters
				leadSource,
				addOn,
				inboundChannel,
				lostReasonCategories,
				industry,
				tag,
				isColdDemo: isColdDemo != null ? String(isColdDemo) : undefined,
				overbook: overbook != null ? String(overbook) : undefined,
				followUpDateStart: followUpDateStart ? formatDate(followUpDateStart, 'yyyy-MM-dd') : undefined,
				followUpDateEnd: followUpDateEnd ? formatDate(followUpDateEnd, 'yyyy-MM-dd') : undefined,
				targetCloseDateStart: targetCloseDateStart ? formatDate(targetCloseDateStart, 'yyyy-MM-dd') : undefined,
				targetCloseDateEnd: targetCloseDateEnd ? formatDate(targetCloseDateEnd, 'yyyy-MM-dd') : undefined,
				closedDateStart: closedDateStart ? formatDate(closedDateStart, 'yyyy-MM-dd') : undefined,
				closedDateEnd: closedDateEnd ? formatDate(closedDateEnd, 'yyyy-MM-dd') : undefined,
			};
			for (const [key, value] of Object.entries(params)) {
				if (value != null && value !== '') {
					searchParams.append(key, value);
				}
			}
			if (dealStage) {
				dealStage.forEach(stage => {
					searchParams.append('dealStage', stage);
				});
			}
			return userSession.webServiceHelper.callAsync<IPagedCollection<ILeadSearchView>>(
				`lead/demos?${searchParams.toString()}`,
				'GET'
			);
		},
		getNextPageParam: lastPage => lastPage.pageToken,
		initialPageParam: '',
		enabled,
		meta,
	});
}
